import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Loader2, Sparkles, Pencil, Check, Trash2, AlertTriangle } from 'lucide-react';
import { PhotoUploadField } from '../PhotoUploadField';

const uploadArtistImage = async (fd) => (await api.adminUploadArtistImage(fd)).data.image_url;

const GENRE_OPTIONS = [
  { key: 'dancehall', label: 'Dancehall' },
  { key: 'reggae', label: 'Reggae' },
  { key: 'soca', label: 'Soca' },
];

export function GuestArtistsTab() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // current guest being edited
  const [form, setForm] = useState({
    name: '', price: '', bio: '', genres: [], image_url: '', delivery_days: 7,
  });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getAdminGuestArtists();
      setGuests(res.data || []);
    } catch {
      toast.error('Failed to load guest artists');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openEditor = (g) => {
    setEditing(g);
    setForm({
      name: g.name || '',
      price: g.price ?? '',
      bio: g.bio || '',
      genres: g.genres || [],
      image_url: g.image_url || '',
      delivery_days: g.delivery_days ?? 7,
    });
  };

  const toggleGenre = (key) => {
    setForm(prev => ({
      ...prev,
      genres: prev.genres.includes(key) ? prev.genres.filter(g => g !== key) : [...prev.genres, key],
    }));
  };

  const handleSave = async () => {
    if (!editing) return;
    const price = parseFloat(form.price);
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!Number.isFinite(price) || price <= 0) { toast.error('Valid price required'); return; }
    setSaving(true);
    try {
      await api.updateAdminGuestArtist(editing.id, {
        name: form.name.trim(),
        price,
        bio: form.bio.trim(),
        genres: form.genres,
        image_url: form.image_url.trim() || undefined,
        delivery_days: parseInt(form.delivery_days, 10) || 7,
      });
      toast.success('Guest artist updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async (g) => {
    if (!window.confirm(`Promote "${g.name}" to a regular artist? They'll appear on the Browse Artists page.`)) return;
    setBusyId(g.id);
    try {
      await api.promoteAdminGuestArtist(g.id);
      toast.success(`${g.name} promoted to full artist`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to promote');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Reject and delete guest artist "${g.name}"?`)) return;
    setBusyId(g.id);
    try {
      await api.deleteAdminGuestArtist(g.id);
      toast.success(`${g.name} removed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-lime" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-lime" /> Guest Artists
        </h2>
        <p className="text-sm text-[#A1A1AA]">
          Agents added these artists for their Weekly Specials lineup. Review, edit, and promote them to full
          Tune4Tune artists so they appear on Browse Artists going forward.
        </p>
      </div>

      {guests.length === 0 ? (
        <div className="text-center py-16 bg-surface rounded-2xl border border-[#262626]">
          <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#333]" />
          <h3 className="text-xl font-heading font-bold mb-2">No Guest Artists Pending</h3>
          <p className="text-[#A1A1AA]">When an agent adds a new artist to Weekly Specials they&apos;ll show up here.</p>
        </div>
      ) : (
        <div className="bg-surface border border-[#262626] rounded-xl overflow-hidden">
          <div className="divide-y divide-[#262626]">
            {guests.map(g => (
              <div key={g.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`guest-artist-row-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold truncate">{g.name}</h3>
                    <Badge className="bg-lime/10 text-lime border-0 text-xs">${g.price?.toFixed?.(2)}</Badge>
                    <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Pending review
                    </Badge>
                  </div>
                  <p className="text-xs text-[#A1A1AA] mt-1 truncate">
                    Added by agent {g.created_by_agent_name || '—'} · {new Date(g.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="border-[#262626]" onClick={() => openEditor(g)} data-testid={`edit-guest-${g.id}`}>
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" className="btn-primary" onClick={() => handlePromote(g)} disabled={busyId === g.id} data-testid={`promote-guest-${g.id}`}>
                    {busyId === g.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Promote
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => handleDelete(g)} disabled={busyId === g.id} data-testid={`delete-guest-${g.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-surface border-[#262626] max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Guest Artist</DialogTitle>
            <DialogDescription>Fill out their profile before promoting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" data-testid="guest-edit-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Price ($)</Label>
                <Input type="number" min={1} step={1} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-dark" data-testid="guest-edit-price" />
              </div>
              <div className="space-y-2">
                <Label>Delivery days</Label>
                <Input type="number" min={1} max={45} value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} className="input-dark" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="input-dark min-h-[80px]" placeholder="Short bio (optional)" />
            </div>
            <div className="space-y-2">
              <Label>Genres</Label>
              <div className="flex flex-wrap gap-2">
                {GENRE_OPTIONS.map(opt => {
                  const active = form.genres.includes(opt.key);
                  return (
                    <button
                      type="button"
                      key={opt.key}
                      onClick={() => toggleGenre(opt.key)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${active ? 'bg-lime text-black border-lime' : 'border-[#262626] text-[#A1A1AA] hover:bg-surface-highlight'}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <PhotoUploadField
              label="Artist Photo"
              value={form.image_url}
              onChange={(url) => setForm((prev) => ({ ...prev, image_url: url }))}
              uploadFn={uploadArtistImage}
              testIdPrefix="guest-image"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#262626]" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="guest-edit-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
